import { LinearProgress } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { styled } from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { Hospital } from '../../../../shared/api/hscan/hospital';
import { BRAND_NAME } from '../../../../shared/env';
import { usePushStudy } from '../../../../shared/hooks/hospitalHook';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import { CheckboxWithText } from '../../common/components/CheckboxWithText';
import { RrnSlot } from '../../common/components/Slot';
import { HospitalAutocompleteSlot } from '../../common/components/Slot/HospitalAutocompleteSlot';
import { Spacer } from '../../common/components/Spacer';
import {
  WideModal,
  ModalTextContainer,
} from '../../common/components/WideModal';
import { TermText } from '../../common/components/styledComponents';
import { useAutoSearchHospital } from '../../hooks/autoSearchHospital';

import { SelectedImageTable } from './SelectedImageTable';

const InstitutionSlotWrapper = styled.div`
  width: 394px;
  max-width: 100%;

  display: flex;
  flex-direction: column;
  row-gap: 26px;
  container-type: inline-size;
`;

interface SendToInstitutionTabProps {
  cases: Case[];
}
export const SendToInstitutionTab = (props: SendToInstitutionTabProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.sendImages',
  });
  const navigate = useNavigate();
  const { cases } = props;
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const hospitalIdQueryParam = queryParams.get('hospitalId');

  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [rrNumber, setRrNumber] = useState('');
  const [checkTerm, setCheckTerm] = useState(false);
  const isSendAvailable = hospital && checkTerm && cases.length;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setCheckTerm(false);
  }, [hospital, cases.length]);

  const initializePage = useCallback(() => {
    setHospital(null);
    setCheckTerm(false);
  }, []);

  const {
    mutate: sendStoreForm,
    isPending,
    isError,
  } = usePushStudy({
    onSuccess() {
      initializePage();
    },
  });
  const onClickSendButton = () => {
    if (hospital?.id) {
      sendStoreForm({
        id: hospital.id,
        caseId: cases.map(c => c.caseId),
        registration: rrNumber,
      });
      setOpen(true);
    }
  };

  const [openCommentModal, setOpenCommentModal] = useState(false);
  const onChangeHospital = (hospital: Hospital | null) => {
    setHospital(hospital);
    setRrNumber('');
    if (hospital && hospital.comment) {
      setOpenCommentModal(true);
    }
  };

  const { abortAutoSearch } = useAutoSearchHospital({
    hospitalId: hospitalIdQueryParam ?? '',
    onRequestHospitalSuccess: setHospital,
    isSearchable: false,
  });

  return (
    <>
      <SelectedImageTable data={cases} />
      <Spacer height={17} />
      <TermText>{t('send.terms.0', { brandName: BRAND_NAME })}</TermText>
      <Spacer height={36} />
      <InstitutionSlotWrapper>
        <HospitalAutocompleteSlot
          label={`${t('send.inputs.hospital.title')}:`}
          value={hospital}
          onChangeHospital={onChangeHospital}
          onChangeQuery={abortAutoSearch}
          storeEnabledOnly
        />
        {hospital?.registrationNumberRequired && (
          <RrnSlot
            label={`${t('send.inputs.rrn.title')}`}
            value={rrNumber}
            onChange={value => setRrNumber(value)}
          />
        )}
      </InstitutionSlotWrapper>
      <Spacer height={21} />
      <CheckboxWithText
        text={<TermText>{t('send.terms.1')}</TermText>}
        value={checkTerm}
        onChange={value => setCheckTerm(value)}
      />
      <BlueRoundedButtonWithIcon
        maxWidth="239px"
        marginTop="38px"
        onClick={onClickSendButton}
        disabled={!isSendAvailable}>
        {t('send.button')}
      </BlueRoundedButtonWithIcon>
      <WideModal
        open={open}
        onClose={() => {
          if (!isPending) {
            setOpen(false);
          }
        }}>
        <ModalTextContainer>
          {isPending ? (
            <>
              {t('send.modal.loadingMessage')}
              <LinearProgress />
            </>
          ) : isError ? (
            t('send.modal.errorMessage')
          ) : (
            <Trans
              t={t}
              i18nKey="send.modal.confirmMessages"
              components={[<br />, <strong />]}
            />
          )}
        </ModalTextContainer>
        {!isPending && (
          <BlueRoundedButtonWithIcon
            marginTop="22px"
            onClick={() => {
              navigate('/uploaded-images');
              setOpen(false);
            }}>
            {t('send.modal.confirmButton')}
          </BlueRoundedButtonWithIcon>
        )}
      </WideModal>
      <WideModal
        open={openCommentModal}
        onClose={() => setOpenCommentModal(false)}>
        <ModalTextContainer>{hospital?.comment}</ModalTextContainer>
      </WideModal>
    </>
  );
};
